/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { KeyRound, Loader2, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const PasswordResetPage = () => {
  usePageTitle('Sifre Sifirla');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Kopyala-yapistir veya autofill kaynakli bosluklari temizle
    const pw = (newPassword || '').trim();
    const cpw = (confirmPassword || '').trim();
    if (pw.length < 8) { setError('Sifre en az 8 karakter olmali'); return; }
    if (!/[A-Za-z]/.test(pw)) { setError('Sifre en az bir harf icermeli'); return; }
    if (!/[0-9]/.test(pw)) { setError('Sifre en az bir rakam icermeli'); return; }
    if (!/[^A-Za-z0-9]/.test(pw)) { setError('Sifre en az bir ozel karakter icermeli (!@#$%^&* vb.)'); return; }
    if (pw !== cpw) { setError('Sifreler eslesmedi'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setSuccess(true);
      toast.success('Sifreniz basariyla degistirildi. Giris ekranina yonlendiriliyorsunuz...');
      setTimeout(() => navigate('/giris', { replace: true }), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Sifre sifirlanamadi');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="py-12 text-center">
            <KeyRound className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Gecersiz Baglanti</h2>
            <p className="text-slate-500 mb-4">Sifre sifirlama baglantisi gecersiz veya eksik.</p>
            <Link to="/giris"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-1" /> Giris Sayfasina Don</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Sifre Degistirildi</h2>
            <p className="text-slate-500 mb-2">Yeni sifreniz ile giris yapabilirsiniz.</p>
            <p className="text-xs text-slate-400 mb-6">Giris ekranina yonlendiriliyorsunuz...</p>
            <Button onClick={() => navigate('/giris', { replace: true })} className="bg-blue-600 hover:bg-blue-700" data-testid="go-login-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Hemen Giris Yap
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
            <KeyRound className="w-7 h-7 text-blue-600" />
          </div>
          <CardTitle>Yeni Sifre Belirle</CardTitle>
          <CardDescription>Yeni sifrenizi girin</CardDescription>
        </CardHeader>
        <CardContent>
          {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Yeni Sifre</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  placeholder="En az 8 karakter, harf + rakam + ozel karakter" required data-testid="reset-new-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Anlik dogrulama gosterimi */}
              {newPassword && (
                <ul className="text-xs mt-1 space-y-0.5">
                  <li className={newPassword.length >= 8 ? 'text-emerald-600' : 'text-red-500'}>
                    {newPassword.length >= 8 ? '✓' : '•'} En az 8 karakter
                  </li>
                  <li className={/[A-Za-z]/.test(newPassword) ? 'text-emerald-600' : 'text-red-500'}>
                    {/[A-Za-z]/.test(newPassword) ? '✓' : '•'} En az bir harf
                  </li>
                  <li className={/[0-9]/.test(newPassword) ? 'text-emerald-600' : 'text-red-500'}>
                    {/[0-9]/.test(newPassword) ? '✓' : '•'} En az bir rakam
                  </li>
                  <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'text-emerald-600' : 'text-red-500'}>
                    {/[^A-Za-z0-9]/.test(newPassword) ? '✓' : '•'} En az bir ozel karakter
                  </li>
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <Label>Sifre Tekrar</Label>
              <Input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Ayni sifreyi tekrar girin" required data-testid="reset-confirm-password" />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">Sifreler eslesmedi</p>
              )}
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="reset-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />} Sifremi Degistir
            </Button>
          </form>
          <div className="text-center mt-4">
            <Link to="/giris" className="text-sm text-blue-600 hover:underline">Giris sayfasina don</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordResetPage;
